// Libs
import {
  Box,
  CircularProgress,
  FormHelperText,
  Grow,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
// Components, Layouts, Pages
import { BaseInputSearch } from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { DEFAULT_CURRENT_PAGE, DEFAULT_DELAY_TIME, EMPTY_STRING } from '~/utils/constants/common';
import { IBaseOption, IOptionSelected, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './BaseDynamicSelect.module.scss';

type Props<T> = {
  defaultSelected?: T[] | null;
  label?: string;
  name?: string;
  value?: string | string[];
  required?: boolean;
  gap?: number;
  multiple?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  isAutoWidth?: boolean;
  placeholder?: string;
  width?: number | string;
  height?: number | string;
  borderRadius?: number;
  isSearch?: boolean;
  iconDropdown?: React.ReactNode;
  labelOptions?: string;
  iconLabel?: string;
  productId?: string;
  itemNameDefault?: string | string[];
  onChange?: (selected: IOptionSelected<T>) => void;
  callApiFn: (payload: IPayloadCallApiFn) => Promise<IResultCallApiFn<T>>;
};

const cx = classNames.bind(styles);

const BaseDynamicSelect = <T extends IBaseOption>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    label,
    name,
    value,
    required = false,
    multiple = false,
    disabled,
    errorMessage,
    gap,
    placeholder,
    isAutoWidth,
    width,
    height,
    borderRadius,
    isSearch,
    iconDropdown,
    productId,
    labelOptions,
    iconLabel,
    defaultSelected,
    itemNameDefault,
    onChange,
    callApiFn,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [valueSelected, setValueSelected] = useState<string | string[]>(multiple ? [] : EMPTY_STRING);
  const [valueDefault, setValueDefault] = useState<T[] | null>(null);
  const [isShowMenu, setIsShowMenu] = useState<boolean>(false);
  const [listOption, setListOption] = useState<T[]>([]);

  const [searchValue, setSearchValue] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce<string>(searchValue.trim(), DEFAULT_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [expanded, setExpanded] = useState(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleFetchData(true);
  }, [debounceSearchKey, productId, callApiFn]);

  useEffect(() => {
    if (!listOption.length) return;

    if (multiple) {
      const valueDefaultFiltered =
        defaultSelected?.filter((item) => !listOption.find((opt) => opt.value === item.value)) || [];

      setValueSelected(value ?? []);
      setValueDefault(valueDefaultFiltered);
      return;
    }

    const compareValue = value ?? defaultSelected?.[0]?.value;
    const matched = listOption.find((item) => String(item.value) === String(compareValue));

    if (matched) {
      setValueSelected(matched.value);
    } else if (defaultSelected) {
      setValueDefault(defaultSelected);
    } else {
      setValueSelected(EMPTY_STRING);
    }
  }, [value, defaultSelected, listOption]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowMenu = () => {
    setIsShowMenu(true);
  };

  const handleCloseMenu = () => {
    setIsShowMenu(false);
  };

  const handleSelectChange = (optionSelected: T) => {
    const selectedValue: string = optionSelected.value;

    if (!multiple) {
      const onChangeValue: IOptionSelected<T> = {
        idSelected: [selectedValue],
        optionsSelected: [optionSelected],
      };
      setValueSelected(optionSelected.value);
      onChange?.(onChangeValue);
      handleCloseMenu();
      return;
    }

    const isArray = Array.isArray(valueSelected);
    const currentValues = isArray ? valueSelected : [];
    const exists = currentValues.includes(selectedValue);
    const newValue = exists ? currentValues.filter((val) => val !== selectedValue) : [...currentValues, selectedValue];
    const newOptions = newValue
      .map((val) => listOption.find((option) => option.value === val))
      .filter((opt): opt is T => !!opt);

    const onChangeValue: IOptionSelected<T> = {
      idSelected: newValue,
      optionsSelected: newOptions,
    };

    setValueSelected(newValue);
    onChange?.(onChangeValue);
  };

  const handleRemoveSelected = (valueToRemove?: string) => {
    if (!multiple) {
      setValueSelected(EMPTY_STRING);
      onChange?.({
        idSelected: [],
        optionsSelected: [],
      });
      return;
    }

    if (!Array.isArray(valueSelected)) return;

    const newValue = valueSelected.filter((val) => val !== valueToRemove);
    const newOptions = newValue
      .map((val) => listOption.find((opt) => opt.value === val))
      .filter((opt): opt is T => !!opt);

    setValueSelected(newValue);
    onChange?.({
      idSelected: newValue,
      optionsSelected: newOptions,
    });
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchValue(searchValue);
  };

  const handleFetchData = async (isFirstLoad: boolean = false) => {
    if (isLoading || (!isFirstLoad && !hasMore)) return;
    setIsLoading(true);

    try {
      const updatedPage: number = isFirstLoad ? DEFAULT_CURRENT_PAGE : page;
      const resp: IResultCallApiFn<T> = await callApiFn({ page: updatedPage, searchKey: debounceSearchKey, productId });
      const { optionsResp, hasMoreResp } = resp;

      setListOption((prev) => (isFirstLoad ? optionsResp : [...prev, ...optionsResp]));
      setHasMore(hasMoreResp);
      setPage(updatedPage + 1);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseDynamicSelectComponent' style={{ width }}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Select
        id={name || 'select'}
        multiple={multiple}
        value={valueSelected || EMPTY_STRING}
        disabled={disabled}
        autoWidth={isAutoWidth}
        fullWidth
        className={cx('selectField', { disabled })}
        style={{ borderRadius, height }}
        open={isShowMenu}
        onOpen={handleShowMenu}
        onClose={handleCloseMenu}
        IconComponent={() => (
          <Box className={cx('selectArrow')}>
            {iconDropdown ? iconDropdown : <img src={icons.commonIconArrowBottom} />}
          </Box>
        )}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          className: cx('menu'),
          PaperProps: {
            className: cx('paper'),
            id: 'paper',
          },
          MenuListProps: {
            className: cx('menuList'),
            autoFocusItem: false,
          },
        }}
        sx={{
          '.MuiSelect-select': {
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingRight: '0 !important',
            minHeight: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
        }}
        displayEmpty
        renderValue={(selected) => {
          if (isLoading && listOption.length === 0) {
            return (
              <Grow in={isLoading} style={{ transformOrigin: '0 0 0' }}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  gap={'4px'}
                  className={cx('selectedOption', { placeholder: true })}
                >
                  <CircularProgress size={14} />
                  {t('common_button_loading')}
                </Stack>
              </Grow>
            );
          }

          if (multiple) {
            const selectedIds = selected as string[];

            const selectedOptions = selectedIds
              .map((val) => listOption.find((option) => option.value === val))
              .filter((opt): opt is T => !!opt);

            if (selectedOptions.length === 0) {
              return (
                <Box className={cx('selectedOption', { placeholder: true })}>
                  {placeholder || t('common_text_placeholder_select')}
                </Box>
              );
            }

            // const displayed = selectedOptions.slice(0, 2);
            // const remainingCount = Math.max(selectedOptions.length - displayed.length, 0);

            return (
              <Box className={cx('multiSelectedWrapper')}>
                {selectedOptions.map((opt) => (
                  <Box key={opt.value} className={cx('selectedTag')}>
                    <div className={cx('contentSelect')}>
                      <span>{opt.label}</span>
                      <Tooltip arrow placement='top' title={t('common_button_remove', { ns: 'common/base' })}>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveSelected(opt.value);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <img
                            src={icons.commonIconCloseRedNoBg}
                            alt={t('common_img_text_alt')}
                            className={cx('iconClose')}
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Box>
                ))}

                {/*{remainingCount > DEFAULT_NUMBER_ZERO && (*/}
                {/*  <div*/}
                {/*    className={cx('btnTextMore')}*/}
                {/*    data-tooltip-id={`selectedMultiple-${genId}`}*/}
                {/*    data-tooltip-place='top'*/}
                {/*  >*/}
                {/*    <span>{`+ ${remainingCount}`}</span>*/}
                {/*  </div>*/}
                {/*)}*/}
              </Box>
            );
          }
        }}
      >
        {(isSearch || labelOptions) && (
          <Box component={'div'} className={cx('inputGroup')} onMouseDown={(e) => e.stopPropagation()}>
            <Typography component={'p'} className={cx('labelOptions')}>
              {labelOptions}
            </Typography>
            <BaseInputSearch
              value={searchValue}
              onChange={handleSearchChange}
              width={'100%'}
              placeholder={t('common_text_search')}
            />
          </Box>
        )}
        <Box>
          <InfiniteScroll
            scrollableTarget='dynamicOptionScrollId'
            dataLength={listOption.length}
            hasMore={hasMore}
            next={handleFetchData}
            height={100}
            loader={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                <CircularProgress size={14} />
              </Stack>
            }
            endMessage={
              listOption.length <= 0 && (
                <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                  <Typography component={'p'} className={cx('noData')}>
                    {t('common_no_data_available')}
                  </Typography>
                </Stack>
              )
            }
          >
            {Array.isArray(listOption) &&
              listOption?.length > 0 &&
              listOption?.map((option) => {
                const selected = multiple ? valueSelected?.includes(option.value) : valueSelected === option.value;
                return (
                  <MenuItem
                    selected={selected}
                    key={option?.value}
                    value={option?.value}
                    className={cx('menuItem')}
                    onClick={() => handleSelectChange(option)}
                  >
                    {option?.label || EMPTY_STRING}
                  </MenuItem>
                );
              })}
          </InfiniteScroll>
        </Box>
      </Select>

      {errorMessage && <FormHelperText className={cx('errorMessage')}>{errorMessage}</FormHelperText>}
    </Box>
  );
};

export default BaseDynamicSelect;
